import { Spacing } from '@persona-id/design-tokens';
import {
  BriefcaseIcon,
  CircleHelpIcon,
  DatabaseIcon,
  DocumentIcon,
  ImageIcon,
  LinkExternalIcon,
  UserIcon,
  UsersIcon,
} from '@persona-id/design-tokens/icons';
import { compact, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import FilterBar from 'dashboard/components/ReportResult/PoliticallyExposedPersonPersonaV2/FilterBar';
import {
  gatherPepType,
  PEPFilterType,
} from 'dashboard/components/ReportResult/PoliticallyExposedPersonPersonaV2/PoliticallyExposedPersonPersonaV2';
import PoliticallyExposedPersonPhotoComparison from 'dashboard/components/ReportResult/PoliticallyExposedPersonPhotoComparison';
import { AttributeToggle } from 'dashboard/components/ReportResult/Profile/Profile';
import { FlagIcon } from 'dashboard/components-lib/FlagIcon/FlagIcon';
import TableLayout, { TableNullState, TableTD, TableTR } from 'dashboard/layouts/TableLayout';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { formatDate, formatReportDates } from 'dashboard/lib/utils/formatters';
import { Environment } from 'dashboard/models/environment';
import {
  PoliticallyExposedPersonAssociate,
  PoliticallyExposedPersonCandidacy,
  PoliticallyExposedPersonMatch,
  PoliticallyExposedPersonPosition,
  PoliticallyExposedPersonSource,
  PostingDataLink,
  ReportPoliticallyExposedPerson,
} from 'dashboard/models/report/politically-exposed-person';
import { RootState } from 'dashboard/redux/store';
import ExpandedHeader from 'dashboard/views/DashboardVerificationAnalyticsChecksV2View/ExpandedHeader';
import Card from 'design-system/components/Card/Card';
import { MatchHighlight } from 'design-system/components/MatchHighlight';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { showMessage } from 'verify/redux/actions/messages';

// Example: 2020-04-11
const RAW_PEP_FORMAT = 'y-L-d';

// Example: Aug 2020
const DESIRED_PEP_FORMAT = 'MMM y';

enum PositionColumns {
  Position = 'position',
  Duration = 'duration',
  Class = 'class',
  Topic = 'topics',
}

enum CandidacyColumns {
  Candidacy = 'candidacy',
  Election = 'election',
  Class = 'class',
  Topic = 'topics',
}

// INTERFACES / PROPS

interface SubmitReportProps {
  templateId: string;
  environmentId: string;
  nameFirst: string;
  nameMiddle?: string;
  countryCode?: string;
  nameLast: string;
}

interface PersonaSourceEntityInfoProps {
  report?: ReportPoliticallyExposedPerson;
  entity: PoliticallyExposedPersonMatch;
  isIgnoredEntity: boolean;
  showTitle?: boolean;
}

interface PositionFilterState {
  sort: PositionColumns;
  isDescending: boolean;
}

interface CandidacyFilterState {
  sort: CandidacyColumns;
  isDescending: boolean;
}

// CUSTOM COMPONENTS

const StyledHeader = styled(ExpandedHeader)`
  font-weight: ${FontWeight.Regular};
  font-size: 14px;
  line-height: 20px;
`;

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt: string) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

const InfoContent = styled.dl`
  display: grid;
  grid-gap: ${Spacing[2]};
  grid-template-columns: repeat(2, 1fr);

  & dt {
    color: ${({ theme }) => `${theme.color.text.secondary}`};
  }
`;

const StyledToolTip = styled(Tooltip)`
  float: right;
`;

const Section = styled.section`
  padding: ${Spacing[4]};
  & > h4 {
    margin-bottom: ${Spacing[3]};
    display: flex;
    align-items: center;
    grid-gap: ${Spacing[1]};
  }

  h4 {
    border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
  }
`;

const ListSection = styled(Section)`
  a {
    display: inline-block;
    &:hover,
    &:active,
    &:focus {
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
      cursor: pointer;
    }

    svg {
      visibility: hidden;
      margin-left: 8px;
    }
    &:hover svg {
      visibility: visible;
    }
  }

  ul {
    padding-left: ${Spacing[5]};
    word-wrap: break-word;
    width: max-content;
  }
`;

const TableSection = styled(Section)`
  tr {
    &:hover,
    &:active,
    &:focus {
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
      cursor: pointer;
    }

    svg {
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
      visibility: hidden;
      margin-left: 8px;
    }
    &:hover svg {
      visibility: visible;
    }
  }
`;

const TooltipWrapper = styled(Paragraph)`
  width: 300px;
`;

const ExpandedIdentity = styled.div`
  display: grid;
  grid-gap: ${Spacing[2]};
`;

const ScrollableDetails = styled.dd`
  ul {
    max-height: 170px;
  }
`;

// UTIL FUNCTIONS
const gatherDateTimes = (startDate: string | undefined, endDate: string | undefined) => {
  if (!startDate) {
    return;
  }

  const parsedStart = formatReportDates(startDate, DESIRED_PEP_FORMAT);
  const parsedEnd = endDate ? formatReportDates(endDate, DESIRED_PEP_FORMAT) : undefined;

  const formattedStart = parsedStart === 'Invalid DateTime' ? startDate : parsedStart;
  const formattedEnd = parsedEnd === 'Invalid DateTime' ? endDate : parsedEnd;

  return endDate ? `${formattedStart} - ${formattedEnd}` : `${formattedStart} - Present`;
};

const TopicStickers = ({ topics }: { topics?: string[] }) => {
  if (topics == null || topics.length <= 0) {
    return <Sticker>Uncategorized</Sticker>;
  }

  return (
    <>
      {uniq(topics).map((topic: string) => {
        return (
          <Sticker
            style={{
              marginRight: '2px',
              marginTop: '2px',
            }}
            key={topic}
          >
            {topic}
          </Sticker>
        );
      })}
    </>
  );
};

function titleizePEP(str: string | undefined) {
  const interpretedClass = str ? str.split('-').slice(1).join(' ') : 'Uncategorized';
  return interpretedClass.charAt(0).toUpperCase() + interpretedClass.slice(1);
}

const birthdateMatch = (
  report: ReportPoliticallyExposedPerson,
  identity: PoliticallyExposedPersonMatch,
) => {
  if (!report.attributes.query.birthdate) {
    return false;
  }

  const parsedBirthdate = DateTime.fromFormat(report.attributes.query.birthdate, RAW_PEP_FORMAT);
  return identity.birthdates?.some((birthdate) => {
    const idBirthdate = DateTime.fromFormat(birthdate, RAW_PEP_FORMAT);
    return (
      // Within += 1 year
      idBirthdate.plus({ year: 1 }) >= parsedBirthdate &&
      parsedBirthdate >= idBirthdate.minus({ year: 1 })
    );
  });
};

function extractSourceUrl(links: PostingDataLink[] | undefined) {
  if (!links) {
    return;
  }
  // Default to not use wikidata
  const bestLink = links.find((link) => !link['default-url'].includes('wikidata')) || links[0];
  if (bestLink) {
    return bestLink['default-url'];
  }
}

function formatDatasourceName(datasourceKey: string, datasource: PoliticallyExposedPersonSource) {
  if (datasourceKey !== 'internal-data-source') {
    return datasource.name;
  }

  try {
    return new URL(datasource.url).host;
  } catch (_e) {
    return 'Internal data source';
  }
}

const PoliticallyExposedPersonV2EntityMatchDetails = ({
  report,
  entity,
  isIgnoredEntity,
  showTitle = true,
}: PersonaSourceEntityInfoProps) => {
  // Filter bar state
  const [selectedFilters, setSelectedFilters] = useState<Set<PEPFilterType>>(
    new Set([PEPFilterType.All]),
  );

  // Grab the current environment
  const currentEnvironment = useSelector<RootState, Environment>(
    (state) => state.currentEnvironment.currentEnvironment,
  );

  // Positions Table State
  const [positionSortState, setPositionSortState] = useState<PositionFilterState>({
    sort: PositionColumns.Duration,
    isDescending: true,
  });

  // Candidacies Table State
  const [candidacySortState, setCandidacySortState] = useState<CandidacyFilterState>({
    sort: CandidacyColumns.Election,
    isDescending: true,
  });

  // TODO: [Jonny] Iterate on the related reports and then enable these
  const ALLOW_RELATED_REPORTS = false;

  // Individual Ignore Modal state
  const [ignoreHitsModal, { enable: showIgnoreHitsModal, disable: hideIgnoreHitsModal }] =
    useToggle(false);

  const profileImageUrl = report?.attributes['profile-image-url'];

  // These are going to be used later when we run a new search
  const templateId = report?.relationships['report-template']?.data.id;
  const hasReportCreatePermission = usePermission(Permission.ReportCreate);

  const selectedSources: Record<string, PoliticallyExposedPersonSource> = entity?.sources || {};

  // State Helpers for the Pills
  const allSelected = selectedFilters.has(PEPFilterType.All);
  const profileSelected = selectedFilters.has(PEPFilterType.Profile);
  const portraitSelected = selectedFilters.has(PEPFilterType.Portrait);
  const positionsSelected = selectedFilters.has(PEPFilterType.Positions);
  const candidaciesSelected = selectedFilters.has(PEPFilterType.Candidacies);
  const rcaSelected = selectedFilters.has(PEPFilterType.Relationships);
  const sourcesSelected = selectedFilters.has(PEPFilterType.RelatedLinks);

  // Content helpers for the filter specific sections
  const hasPositions = entity?.positions && entity.positions.length > 0;
  const hasCandidacies = entity?.candidacies && entity.candidacies.length > 0;
  const hasImages = entity?.images && entity.images.length > 0;
  const hasRelationships = entity?.associates && entity.associates.length > 0;

  const handleSubmitReport = async ({
    environmentId,
    templateId,
    nameFirst,
    nameMiddle = '',
    nameLast,
    countryCode,
  }: SubmitReportProps) => {
    const payload = {
      type: ApiModel.ReportPoliticallyExposedPerson as const,
      attributes: {
        'environment-id': environmentId,
        'report-template-id': templateId,
        query: {
          'name-first': nameFirst,
          'name-middle': nameMiddle,
          'name-last': nameLast,
          'country-code': countryCode,
        },
      },
    };

    try {
      const response =
        await SessionClientDashboard.postResourceCreate<ReportPoliticallyExposedPerson>(payload, {
          contentType: 'multipart/form-data',
        });

      window.open(response.data.id);
    } catch {
      showMessage('Failed to create Associated PEP Report');
    }
  };

  // Sort handler
  const handleColumnHeaderClick = <T extends string>(
    columnName: T,
    currentSortState: { sort: T; isDescending: boolean },
    setSortState: (state: { sort: T; isDescending: boolean }) => void,
  ) => {
    setSortState({
      sort: columnName,
      isDescending: columnName === currentSortState.sort ? !currentSortState.isDescending : true,
    });
  };

  const handlePositionColumnHeaderClick = (columnName: PositionColumns) => {
    handleColumnHeaderClick(columnName, positionSortState, setPositionSortState);
  };

  const handleCandidacyColumnHeaderClick = (columnName: CandidacyColumns) => {
    handleColumnHeaderClick(columnName, candidacySortState, setCandidacySortState);
  };

  // Actually sort these positions
  const sortedPositions = useMemo(() => {
    // We need to make a shallow copy as this position array could be read only
    return [...(entity?.positions ?? [])].sort(
      (a: PoliticallyExposedPersonPosition, b: PoliticallyExposedPersonPosition) => {
        switch (positionSortState.sort) {
          case PositionColumns.Class:
            return positionSortState.isDescending
              ? a['pep-class'].localeCompare(b['pep-class'])
              : b['pep-class'].localeCompare(a['pep-class']);
          case PositionColumns.Duration: {
            const dateA = a['end-date']
              ? DateTime.fromISO(a['end-date']).toMillis()
              : Number.POSITIVE_INFINITY;
            const dateB = b['end-date']
              ? DateTime.fromISO(b['end-date']).toMillis()
              : Number.POSITIVE_INFINITY;

            return positionSortState.isDescending ? dateB - dateA : dateA - dateB;
          }
          case PositionColumns.Position:
            return positionSortState.isDescending
              ? a['name'].localeCompare(b['name'])
              : b['name'].localeCompare(a['name']);
          case PositionColumns.Topic:
            return positionSortState.isDescending
              ? JSON.stringify(a['topics']).localeCompare(JSON.stringify(b['topics']))
              : JSON.stringify(b['topics']).localeCompare(JSON.stringify(a['topics']));
        }
      },
    );
  }, [entity?.positions, positionSortState]);

  const sortedCandidacies = useMemo(() => {
    // We need to make a shallow copy as this position array could be read only
    return [...(entity?.candidacies ?? [])].sort(
      (a: PoliticallyExposedPersonCandidacy, b: PoliticallyExposedPersonCandidacy) => {
        switch (candidacySortState.sort) {
          case CandidacyColumns.Class:
            return candidacySortState.isDescending
              ? a['pep-class'].localeCompare(b['pep-class'])
              : b['pep-class'].localeCompare(a['pep-class']);
          case CandidacyColumns.Election:
            return candidacySortState.isDescending
              ? DateTime.fromFormat(b['end-date'], 'y').toMillis() -
                  DateTime.fromFormat(a['end-date'], 'y').toMillis()
              : DateTime.fromFormat(a['end-date'], 'y').toMillis() -
                  DateTime.fromFormat(b['end-date'], 'y').toMillis();
          case CandidacyColumns.Candidacy:
            return candidacySortState.isDescending
              ? a['name'].localeCompare(b['name'])
              : b['name'].localeCompare(a['name']);
          case CandidacyColumns.Topic:
            return candidacySortState.isDescending
              ? JSON.stringify(a['topics']).localeCompare(JSON.stringify(b['topics']))
              : JSON.stringify(b['topics']).localeCompare(JSON.stringify(a['topics']));
        }
      },
    );
  }, [entity?.candidacies, candidacySortState]);

  return (
    <ExpandedIdentity>
      {showTitle && (
        <Heading size='sm' data-chromatic='ignore' style={{ display: 'flex' }}>
          {entity?.name}
          {!isIgnoredEntity && report && (
            <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
              <ContextMenu
                variant='inverse'
                items={[
                  {
                    id: 'reports-ignore-hits-modal__dismiss-hits-button',
                    onClick: showIgnoreHitsModal,
                    hidden: false,
                    label: 'Dismiss match',
                  },
                ]}
                renderItem={(item) => (
                  <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                    {item.label}
                  </ContextMenu.Item>
                )}
              />
              <IgnoreHitsModal
                report={report}
                ignoreType='entity'
                entityId={entity['entity-id']}
                ignoreHitsModalOpen={ignoreHitsModal}
                hideIgnoreHitsModal={hideIgnoreHitsModal}
              />
            </Permissioned>
          )}
        </Heading>
      )}
      <FilterBar
        filterOptions={PEPFilterType}
        filterState={selectedFilters}
        availableFilters={compact([
          PEPFilterType.All,
          PEPFilterType.Profile,
          PEPFilterType.RelatedLinks,
          // We only render the positions, relationships and portrait when they're present
          hasPositions ? PEPFilterType.Positions : null,
          hasCandidacies ? PEPFilterType.Candidacies : null,
          hasImages ? PEPFilterType.Portrait : null,
          hasRelationships ? PEPFilterType.Relationships : null,
        ])}
        setFilterState={setSelectedFilters}
      />
      {(allSelected || profileSelected) && (
        <>
          <Section>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              <Sticker icon={<UserIcon />} /> Profile
            </Paragraph>
            <InfoContent>
              <dt>Full name</dt>
              <dd>
                <MatchHighlight
                  matchStrength={
                    entity['match-types']?.includes('name_exact') ||
                    entity['match-types']?.includes('name_fuzzy')
                      ? 'full'
                      : undefined
                  }
                >
                  <AttributeToggle
                    key='profile-name-full'
                    id='profile-name-full'
                    items={[entity.name]}
                  />
                </MatchHighlight>
              </dd>
              <dt>Aliases</dt>
              <MatchHighlight
                matchStrength={
                  entity['match-types']?.includes('name_exact') ||
                  entity['match-types']?.includes('name_fuzzy')
                    ? undefined
                    : 'full'
                }
              >
                <ScrollableDetails>
                  <AttributeToggle
                    key='profile-name-full'
                    id='profile-name-full'
                    items={entity.aliases ?? []}
                  />
                </ScrollableDetails>
              </MatchHighlight>
              <dt>Birthdate</dt>
              <MatchHighlight
                matchStrength={report && birthdateMatch(report, entity) ? 'full' : undefined}
              >
                <dd>
                  <AttributeToggle
                    key='profile-birthdates'
                    id='profile-birthdates'
                    items={
                      entity.birthdates?.map((birthdate) => {
                        // When it only has year precision, only return the year
                        if (birthdate?.length === 4) return birthdate;
                        return formatDate(birthdate);
                      }) || []
                    }
                  />
                </dd>
              </MatchHighlight>
              <dt>Sex</dt>
              <dd>
                <AttributeToggle
                  key='profile-birthdates'
                  id='profile-birthdates'
                  items={entity?.sex ? [toTitleCase(entity?.sex)] : []}
                />
              </dd>
              <dt>Type</dt>
              <dd>{gatherPepType(entity && entity['pep-type'] ? entity['pep-type'] : '')}</dd>
              <dt>Categories</dt>
              <dd>
                <TopicStickers
                  topics={[
                    ...(entity.positions?.flatMap((position) => position.topics) || []),
                    ...(entity.candidacies?.flatMap((candidacy) => candidacy.topics) || []),
                  ]}
                />
              </dd>
              <dt>Nationality</dt>
              <dd>
                {entity?.locations?.map((country) => (
                  <div key='country-${country}'>
                    <FlagIcon key={country} flagCode={country} border={true} />
                  </div>
                ))}
              </dd>
            </InfoContent>
          </Section>
          <Spacer height={1} />
        </>
      )}
      {hasImages && (allSelected || portraitSelected) && (
        <>
          <Section>
            {profileImageUrl ? (
              <Card.SectionHeader>
                <Sticker icon={<ImageIcon />} /> Portrait similarity
                <Tooltip side='top' iconTrigger={<CircleHelpIcon />}>
                  <TooltipWrapper size='sm'>
                    Portrait similarity shows whether the end user&rsquo;s portrait resembles
                    publicly available photos.
                  </TooltipWrapper>
                </Tooltip>
              </Card.SectionHeader>
            ) : (
              <Card.SectionHeader>
                <Sticker icon={<ImageIcon />} /> Portrait
              </Card.SectionHeader>
            )}
            <PoliticallyExposedPersonPhotoComparison
              referenceImages={entity.images}
              profileImageUrl={profileImageUrl}
            />
          </Section>
          <Spacer height={1} />
        </>
      )}
      {hasPositions && (allSelected || positionsSelected) && (
        <>
          <TableSection>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              <Sticker icon={<BriefcaseIcon />} /> Positions
            </Paragraph>
            <TableLayout
              showHeader={true}
              nullState={
                <TableNullState hasUsedFilters={true} dataTestBlock='checks-view'>
                  <div>No Positions.</div>
                </TableNullState>
              }
              headerTR={
                <>
                  <StyledHeader
                    id='pep-report-identity_position-header'
                    canSort={true}
                    isSorted={positionSortState.sort === PositionColumns.Position}
                    isDescending={positionSortState.isDescending}
                    onClick={() => handlePositionColumnHeaderClick(PositionColumns.Position)}
                  >
                    Position
                  </StyledHeader>
                  <StyledHeader
                    id='pep-report-identity_duration-header'
                    canSort={true}
                    isSorted={positionSortState.sort === PositionColumns.Duration}
                    isDescending={positionSortState.isDescending}
                    onClick={() => handlePositionColumnHeaderClick(PositionColumns.Duration)}
                  >
                    Duration
                  </StyledHeader>
                  <StyledHeader
                    id='pep-report-identity_class-header'
                    canSort={true}
                    isSorted={positionSortState.sort === PositionColumns.Class}
                    isDescending={positionSortState.isDescending}
                    onClick={() => handlePositionColumnHeaderClick(PositionColumns.Class)}
                  >
                    Class
                  </StyledHeader>

                  <StyledHeader
                    id='pep-report-identity_source-header'
                    canSort={true}
                    isSorted={positionSortState.sort === PositionColumns.Topic}
                    isDescending={positionSortState.isDescending}
                    onClick={() => handlePositionColumnHeaderClick(PositionColumns.Topic)}
                  >
                    Category
                  </StyledHeader>
                </>
              }
            >
              {sortedPositions.map((position: PoliticallyExposedPersonPosition, idx) => (
                <TableTR
                  key={`position-${idx}`}
                  onClick={() =>
                    extractSourceUrl(position['dataset-links'])
                      ? window.open(extractSourceUrl(position['dataset-links']))
                      : undefined
                  }
                >
                  <TableTD>{toTitleCase(position.name)}</TableTD>
                  <TableTD>{gatherDateTimes(position['start-date'], position['end-date'])}</TableTD>
                  <TableTD>
                    <TopicStickers topics={position['pep-class']?.split(', ')?.map(titleizePEP)} />
                  </TableTD>
                  <TableTD>
                    <div style={{ display: 'flex' }}>
                      {position.topics?.length > 0 ? (
                        <Sticker> {position.topics[0]} </Sticker>
                      ) : (
                        <></>
                      )}
                      {position['dataset-links'] && position['dataset-links'].length > 0 ? (
                        <LinkExternalIcon />
                      ) : (
                        ''
                      )}
                    </div>
                  </TableTD>
                </TableTR>
              ))}
            </TableLayout>
          </TableSection>
          <Spacer height={1} />
        </>
      )}
      {hasRelationships && (allSelected || rcaSelected) && (
        <>
          <Section>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              <Sticker icon={<UsersIcon />} /> Relationships
            </Paragraph>
            <TableLayout
              showHeader={true}
              nullState={
                <TableNullState hasUsedFilters={true} dataTestBlock='checks-view'>
                  <div>No Associates.</div>
                </TableNullState>
              }
              headerTR={
                <>
                  <StyledHeader id='pep-report-identity_name-header' canSort={false}>
                    Associated with
                  </StyledHeader>
                  <StyledHeader id='pep-report-identity_relation-header' canSort={false}>
                    Relation
                  </StyledHeader>
                  <StyledHeader id='pep-report-identity_class-header' canSort={false}>
                    Class
                  </StyledHeader>
                  <StyledHeader id='pep-report-identity_type-header' canSort={false}>
                    Type
                  </StyledHeader>
                </>
              }
            >
              {entity?.associates?.map((associate: PoliticallyExposedPersonAssociate, idx) => {
                const names = associate.name.split(' ');

                let nameMiddle: string | undefined;
                const nameFirst = names[0];
                const nameLast = names[names.length - 1];

                // When there are middle names, lets grab all of them and put them together
                if (names.length > 2) {
                  nameMiddle = names.slice(1, -1).join(' ');
                }

                return (
                  <TableTR
                    key={`relationship-${idx}`}
                    onClick={() =>
                      templateId && hasReportCreatePermission && ALLOW_RELATED_REPORTS
                        ? handleSubmitReport({
                            environmentId: currentEnvironment.id,
                            nameFirst,
                            nameMiddle,
                            nameLast,
                            templateId,
                          })
                        : undefined
                    }
                  >
                    <TableTD>{associate.name}</TableTD>
                    <TableTD>{associate.relationship}</TableTD>
                    <TableTD>
                      <Sticker>{titleizePEP(associate['pep-class'])}</Sticker>
                    </TableTD>
                    <TableTD>
                      <TopicStickers topics={associate['topics']} />
                      {ALLOW_RELATED_REPORTS && (
                        <StyledToolTip iconTrigger={<DocumentIcon />}>
                          <TooltipWrapper size='sm'>
                            Create PEP Report on the associated relative using the same Report
                            Template.
                          </TooltipWrapper>
                        </StyledToolTip>
                      )}
                    </TableTD>
                  </TableTR>
                );
              })}
            </TableLayout>
          </Section>
          <Spacer height={1} />
        </>
      )}
      {hasCandidacies && (allSelected || candidaciesSelected) && (
        <>
          <TableSection>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              <Sticker icon={<BriefcaseIcon />} /> Candidacies
            </Paragraph>
            <TableLayout
              showHeader={true}
              nullState={
                <TableNullState hasUsedFilters={true} dataTestBlock='checks-view'>
                  <div>No Candidacies.</div>
                </TableNullState>
              }
              headerTR={
                <>
                  <StyledHeader
                    id='pep-report-identity-candidacy_candidacy-header'
                    canSort={true}
                    isSorted={candidacySortState.sort === CandidacyColumns.Candidacy}
                    isDescending={candidacySortState.isDescending}
                    onClick={() => handleCandidacyColumnHeaderClick(CandidacyColumns.Candidacy)}
                  >
                    Candidacy
                  </StyledHeader>
                  <StyledHeader
                    id='pep-report-identity-candidacy_election-header'
                    canSort={true}
                    isSorted={candidacySortState.sort === CandidacyColumns.Election}
                    isDescending={candidacySortState.isDescending}
                    onClick={() => handleCandidacyColumnHeaderClick(CandidacyColumns.Election)}
                  >
                    Election period
                  </StyledHeader>
                  <StyledHeader
                    id='pep-report-identity-candidacy_class-header'
                    canSort={true}
                    isSorted={candidacySortState.sort === CandidacyColumns.Class}
                    isDescending={candidacySortState.isDescending}
                    onClick={() => handleCandidacyColumnHeaderClick(CandidacyColumns.Class)}
                  >
                    Class
                  </StyledHeader>

                  <StyledHeader
                    id='pep-report-identity-candidacy_source-header'
                    canSort={true}
                    isSorted={candidacySortState.sort === CandidacyColumns.Topic}
                    isDescending={candidacySortState.isDescending}
                    onClick={() => handleCandidacyColumnHeaderClick(CandidacyColumns.Topic)}
                  >
                    Category
                  </StyledHeader>
                </>
              }
            >
              {sortedCandidacies.map((candidacy: PoliticallyExposedPersonCandidacy, idx) => (
                <TableTR
                  key={`candidacy-${idx}`}
                  onClick={() =>
                    extractSourceUrl(candidacy['dataset-links'])
                      ? window.open(extractSourceUrl(candidacy['dataset-links']))
                      : undefined
                  }
                >
                  <TableTD>{toTitleCase(candidacy.name)}</TableTD>
                  <TableTD>{formatReportDates(candidacy['end-date'], 'y')}</TableTD>
                  <TableTD>
                    <TopicStickers topics={candidacy['pep-class']?.split(', ')?.map(titleizePEP)} />
                  </TableTD>
                  <TableTD>
                    <div style={{ display: 'flex' }}>
                      {candidacy.topics?.length > 0 ? (
                        <Sticker> {candidacy.topics[0]} </Sticker>
                      ) : (
                        <></>
                      )}
                      {candidacy['dataset-links'] && candidacy['dataset-links'].length > 0 ? (
                        <LinkExternalIcon />
                      ) : (
                        ''
                      )}
                    </div>
                  </TableTD>
                </TableTR>
              ))}
            </TableLayout>
          </TableSection>
          <Spacer height={1} />
        </>
      )}
      {(allSelected || sourcesSelected) && (
        <>
          <ListSection>
            <Paragraph size='lg' weight='bold' color='primary' as='h4'>
              <Sticker icon={<DatabaseIcon />} />
              Related links
            </Paragraph>
            {selectedSources && (
              <ul>
                {Object.keys(selectedSources).map((datasourceKey, idx) => {
                  return (
                    <li key={`pep-report-position-source-${idx}`}>
                      {selectedSources[datasourceKey].url ? (
                        <a
                          href={selectedSources[datasourceKey].url}
                          target='_blank'
                          rel='noreferrer noopener'
                        >
                          {formatDatasourceName(datasourceKey, selectedSources[datasourceKey])}
                        </a>
                      ) : (
                        formatDatasourceName(datasourceKey, selectedSources[datasourceKey])
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </ListSection>
          <Spacer height={1} />
        </>
      )}
    </ExpandedIdentity>
  );
};
export default PoliticallyExposedPersonV2EntityMatchDetails;
